/**
 * <favorites-drawer>
 *
 * Extends DrawerComponent — uses shared drawer open/close + slide animation.
 * Markup: sections/main-favorites_drawer.liquid (`Drawer Drawer--right`).
 *
 * Quick-add default selection attribute:
 * - data-quick-atc-default-config: JSON with target position, preferred value,
 *   and newline-separated fallback priority
 */

import { addVariantToCart } from '@theme/cart-api';
import { Delegate } from '@theme/delegate';
import { DrawerComponent } from '@theme/drawer-component';
import {
  getFavorites,
  getFavoritesCount,
  hasSelectedVariant,
  removeFavorite,
  FAVORITES_CHANGED_EVENT,
} from '@theme/favorites-storage';
import {
  getRecentlyViewedHandles,
  RECENTLY_VIEWED_CHANGED_EVENT,
  FAVORITES_RECENTLY_VIEWED_LIMIT,
} from '@theme/recently-viewed';
import { isLowStock } from '@theme/low-stock';
import { formatMoney } from '@theme/money';
const IMAGE_WIDTHS = [320, 480, 750, 1000, 1200, 1500, 2000];
// Product attrs the analytics singleton reads from a list unit (data-product-id etc.).
const PRODUCT_DATASET_KEYS = ['productId', 'productTitle', 'variantId', 'price', 'productType'];
const STOCK_CACHE_TTL_MS = 60_000;

class FavoritesDrawer extends DrawerComponent {
  #delegate = null;
  #strings = {};
  #productCache = new Map();
  #mediaCache = new Map();
  #stockCache = new Map();
  #stockRequests = new Map();
  #drawerVideos = new Set();
  #drawerVideoObserver = null;
  #renderToken = 0;
  #recentlyViewedToken = 0;
  #onFavoritesChanged = null;
  #onRecentlyViewedChanged = null;
  #fetchAbortController = null;
  #recentlyViewedAbortController = null;
  #stockAbortController = null;
  #renderedKey = '';
  #quickAtcOpen = false;
  #quickAtcProduct = null;
  #quickAtcStoredItem = null;
  #quickAtcVariants = [];
  #quickAtcSubmitting = false;
  #quickAtcErrorTimeout = null;
  #quickAtcCloseTimeout = null;
  #quickAtcStockRequestToken = 0;
  #quickAtcDefaultConfig = null;
  #quickAtcDefaultSelectionIsAutomatic = false;

  connectedCallback() {
    super.connectedCallback();

    this.#strings = this.#parseStrings();
    this.#quickAtcDefaultConfig = this.#parseQuickAtcDefaultConfig();
    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-action="remove-favorite"]', this.#onRemove);
    this.#delegate.on('click', '[data-favorites-secondary-action]', this.#onSecondaryActionClick);
    this.#delegate.on('click', '[data-action="close-favorites-quick-atc"]', this.#onCloseQuickAtc);
    this.#delegate.on('click', '[data-favorites-quick-atc-submit]', this.#onQuickAtcSubmit);
    this.#delegate.on('change', '[data-favorites-quick-atc] .pdp-variant-option__input', this.#onQuickAtcOptionChange);

    this.#onFavoritesChanged = () => {
      this.#updateHeaderCount();
      void this.#render();
    };

    this.#onRecentlyViewedChanged = () => {
      if (getFavorites().length === 0 && this.dataset.showRecentlyViewed === 'true') {
        void this.#renderRecentlyViewed(
          this.querySelector('[data-favorites-recently-viewed]'),
        );
      }
    };

    document.addEventListener(FAVORITES_CHANGED_EVENT, this.#onFavoritesChanged);
    document.addEventListener(RECENTLY_VIEWED_CHANGED_EVENT, this.#onRecentlyViewedChanged);
    this.#updateHeaderCount();
    void this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortAllFetches();
    this.#recentlyViewedAbortController?.abort();
    this.#abortStockFetches();
    this.#delegate?.off();
    this.#clearDrawerVideos();
    document.removeEventListener(FAVORITES_CHANGED_EVENT, this.#onFavoritesChanged);
    document.removeEventListener(RECENTLY_VIEWED_CHANGED_EVENT, this.#onRecentlyViewedChanged);
    clearTimeout(this.#quickAtcErrorTimeout);
    clearTimeout(this.#quickAtcCloseTimeout);
    this.#closeQuickAtc(true);
  }

  onBeforeClose() {
    this.#closeQuickAtc(true);
  }

  onOpened() {
    if (this.#isStale()) {
      void this.#render();
    } else {
      this.refreshScrollableElement();
      this.#playVisibleDrawerVideos();
    }
  }

  #parseStrings() {
    try {
      return JSON.parse(this.getAttribute('data-strings') || '{}');
    } catch {
      return {};
    }
  }

  #parseQuickAtcDefaultConfig() {
    try {
      const config = JSON.parse(this.getAttribute('data-quick-atc-default-config') || '{}');
      const position = Number(config.position);
      if (!Number.isInteger(position) || position < 1 || position > 3) return null;

      const values = [config.value, ...(config.fallbackPriority || '').split(/\r?\n/)]
        .map((value) => value?.trim())
        .filter(Boolean)
        .filter(
          (value, index, entries) =>
            entries.findIndex((entry) => entry.toLowerCase() === value.toLowerCase()) === index,
        );

      return values.length > 0 ? { position: String(position), values } : null;
    } catch {
      return null;
    }
  }

  #updateHeaderCount() {
    const count = getFavoritesCount();
    const countEl = this.querySelector('[data-favorites-count]');
    const openLabel = this.#strings.open || 'Open wishlist';
    const ariaLabel = count > 0 ? `${openLabel} (${count})` : openLabel;

    if (countEl) {
      countEl.textContent = count > 0 ? `(${count})` : '';
      countEl.hidden = count <= 0;
    }

    document.querySelectorAll('[data-header-favorites-count]').forEach((el) => {
      el.textContent = count > 0 ? `(${count})` : '';
      el.hidden = count <= 0;
    });

    document.querySelectorAll('.header-wishlist-btn-desk').forEach((trigger) => {
      trigger.setAttribute('aria-label', ariaLabel);
    });
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #isAbortError(error) {
    return error?.name === 'AbortError';
  }

  #renewRecentlyViewedSignal() {
    this.#recentlyViewedAbortController?.abort();
    this.#recentlyViewedAbortController = new AbortController();
    return this.#recentlyViewedAbortController.signal;
  }

  #hideRecentlyViewed(container) {
    if (!container) return;

    container.hidden = true;
    container.replaceChildren();
    container.classList.remove(
      'favorites-drawer__recently-viewed--visible',
      'favorites-drawer__recently-viewed--loading',
    );
  }

  #renewFetchSignal() {
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();
    return this.#fetchAbortController.signal;
  }

  #abortAllFetches() {
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = null;
  }

  #getStockFetchSignal() {
    if (!this.#stockAbortController || this.#stockAbortController.signal.aborted) {
      this.#stockAbortController = new AbortController();
    }

    return this.#stockAbortController.signal;
  }

  #abortStockFetches() {
    this.#stockAbortController?.abort();
    this.#stockAbortController = null;
    this.#stockRequests.clear();
  }

  #getFavoritesKey() {
    return getFavorites()
      .map((item) => String(item.variantId))
      .join(',');
  }

  #isStale() {
    return this.#renderedKey !== this.#getFavoritesKey();
  }

  #normalizeImageSrc(src) {
    if (!src) return '';
    if (src.startsWith('//')) return `https:${src}`;
    return src;
  }

  #getFeaturedMediaEntry(product, variant) {
    const media = product?.media;
    if (!Array.isArray(media) || !media.length) return null;

    const variantImageId = variant?.featured_image?.id || variant?.image_id;
    if (variantImageId) {
      const matched = media.find((entry) => String(entry.id) === String(variantImageId));
      if (matched) return matched;
    }

    return media[0];
  }

  #resolveProductPoster(product, variant) {
    const variantImage = variant?.featured_image?.src;
    if (variantImage) return this.#normalizeImageSrc(variantImage);

    if (typeof product?.featured_image === 'string' && product.featured_image) {
      return this.#normalizeImageSrc(product.featured_image);
    }

    const firstImage = product?.images?.[0];
    if (typeof firstImage === 'string' && firstImage) {
      return this.#normalizeImageSrc(firstImage);
    }
    if (firstImage?.src) return this.#normalizeImageSrc(firstImage.src);

    const target = this.#getFeaturedMediaEntry(product, variant);
    const preview = target?.preview_image;

    if (preview?.src) return this.#normalizeImageSrc(preview.src);
    if (typeof preview === 'string') return this.#normalizeImageSrc(preview);
    if (target?.media_type === 'image' && target?.src) {
      return this.#normalizeImageSrc(target.src);
    }

    return '';
  }

  #resolveProductMedia(product, variant) {
    const target = this.#getFeaturedMediaEntry(product, variant);

    if (target?.media_type === 'video') {
      return {
        type: 'video',
        poster: this.#normalizeImageSrc(target.preview_image?.src || ''),
        sources: target.sources || [],
        aspectRatio: target.aspect_ratio,
      };
    }

    if (target?.media_type === 'external_video') {
      return {
        type: 'external_video',
        poster: this.#normalizeImageSrc(target.preview_image?.src || ''),
        externalHost: target.host,
        externalId: target.external_id,
        aspectRatio: target.aspect_ratio,
      };
    }

    const poster = this.#resolveProductPoster(product, variant);
    if (poster) {
      return {
        type: 'image',
        poster,
        aspectRatio: target?.aspect_ratio,
      };
    }

    return null;
  }

  #normalizeFetchedMedia(data) {
    if (!data?.poster && !data?.mediaType) return null;

    const mediaType = data.mediaType || 'image';

    return {
      type:
        mediaType === 'video'
          ? 'video'
          : mediaType === 'external_video'
            ? 'external_video'
            : 'image',
      poster: this.#normalizeImageSrc(data.poster || ''),
      sources: Array.isArray(data.sources) ? data.sources : [],
      aspectRatio: data.aspectRatio,
      externalHost: data.externalHost,
      externalId: data.externalId,
    };
  }

  async #fetchProductMedia(handle, variantId, signal) {
    const cacheKey = `${handle}:${variantId || 'default'}`;
    if (this.#mediaCache.has(cacheKey)) {
      return this.#mediaCache.get(cacheKey);
    }

    const localeRoot = window.routes.localeRootUrl || '';
    const params = new URLSearchParams({
      section_id: 'helper-favorites-product-image',
    });

    if (variantId) params.set('variant', String(variantId));

    const promise = fetch(
      `${localeRoot}/products/${encodeURIComponent(handle)}?${params.toString()}`,
      { signal },
    )
      .then(async (response) => {
        if (!response.ok) return null;

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const jsonEl = doc.querySelector('[data-favorites-product-media-json]');
        if (!jsonEl) return null;

        const media = this.#normalizeFetchedMedia(JSON.parse(jsonEl.textContent.trim()));
        const hasPlayableVideo =
          media?.type !== 'video' || Boolean(this.#pickBestMp4Source(media.sources || []));

        return media && hasPlayableVideo ? media : null;
      })
      .catch((error) => {
        this.#mediaCache.delete(cacheKey);
        if (this.#isAbortError(error)) return null;
        return null;
      });

    this.#mediaCache.set(cacheKey, promise);
    return promise;
  }

  // "Flat shot" = packshot (garment alone, no model). No metafield backs it - per the Figma
  // note the client keeps it as the LAST product image, so that is the contract. Scans media
  // back-to-front for the last still, since a trailing video must not win over an image.
  // Returns null for products with no image at all, which keeps the video path below live.
  #resolveFlatShotMedia(product) {
    const media = Array.isArray(product?.media) ? product.media : [];

    for (let index = media.length - 1; index >= 0; index -= 1) {
      const entry = media[index];
      if (entry?.media_type !== 'image') continue;

      const poster = this.#normalizeImageSrc(entry.preview_image?.src || entry.src || '');
      if (poster) return { type: 'image', poster, aspectRatio: entry.aspect_ratio };
    }

    const images = Array.isArray(product?.images) ? product.images : [];
    const last = images[images.length - 1];
    const poster = this.#normalizeImageSrc(typeof last === 'string' ? last : last?.src || '');

    return poster ? { type: 'image', poster } : null;
  }

  async #getProductMedia(product, variant, signal) {
    const flatShot = this.#resolveFlatShotMedia(product);
    if (flatShot) return flatShot;

    const resolved = this.#resolveProductMedia(product, variant);

    if (resolved?.type === 'video') {
      if (resolved.sources?.length) return resolved;
      const fetched = await this.#fetchProductMedia(product.handle, variant?.id, signal);
      return fetched || resolved;
    }

    if (resolved?.type === 'external_video' && resolved.externalHost && resolved.externalId) {
      return resolved;
    }

    if (resolved?.poster) return resolved;

    return this.#fetchProductMedia(product.handle, variant?.id, signal);
  }

  #pickBestMp4Source(sources) {
    let best = null;

    sources.forEach((source) => {
      if (!source?.url) return;

      const mime = String(source.mime_type || '').toLowerCase();
      const format = String(source.format || '').toLowerCase();
      const url = source.url.toLowerCase();
      const isPlayable =
        format === 'mp4' ||
        mime.includes('mp4') ||
        mime.startsWith('video/') ||
        url.includes('.mp4');

      if (!isPlayable) return;
      if (!best || Number(source.height || 0) > Number(best.height || 0)) best = source;
    });

    return best?.url ? this.#normalizeImageSrc(best.url) : '';
  }

  #getExternalVideoEmbedUrl(host, id) {
    if (!host || !id) return '';

    if (host === 'youtube') {
      return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&playsinline=1&rel=0&modestbranding=1`;
    }

    if (host === 'vimeo') {
      return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&background=1`;
    }

    return '';
  }

  #hideImageElement(imageEl) {
    if (!imageEl) return;

    imageEl.hidden = true;
    imageEl.removeAttribute('src');
    imageEl.removeAttribute('srcset');
    imageEl.alt = '';
  }

  #resetMediaElements(imageEl, videoEl, embedEl) {
    if (imageEl) {
      imageEl.hidden = false;
      imageEl.removeAttribute('src');
      imageEl.removeAttribute('srcset');
    }

    if (videoEl) {
      videoEl.hidden = true;
      videoEl.pause();
      this.#drawerVideoObserver?.unobserve(videoEl);
      this.#drawerVideos.delete(videoEl);
      videoEl.removeAttribute('src');
      videoEl.removeAttribute('poster');
      videoEl.load();
    }

    if (embedEl) {
      embedEl.hidden = true;
      embedEl.replaceChildren();
    }
  }

  #mountProductMedia({ imageEl, videoEl, embedEl, media, alt, sizes }) {
    if (!media) return;

    this.#resetMediaElements(imageEl, videoEl, embedEl);

    if (media.type === 'video') {
      const src = this.#pickBestMp4Source(media.sources || []);
      if (src && videoEl) {
        this.#hideImageElement(imageEl);
        videoEl.hidden = false;
        videoEl.poster = media.poster || '';
        videoEl.src = `${src.split('#')[0]}#t=0.001`;
        videoEl.muted = true;
        videoEl.defaultMuted = true;
        videoEl.loop = true;
        videoEl.playsInline = true;
        videoEl.autoplay = true;
        videoEl.setAttribute('aria-label', alt || '');
        this.#registerDrawerVideo(videoEl);
        return;
      }
    }

    if (media.type === 'external_video' && embedEl) {
      const embedUrl = this.#getExternalVideoEmbedUrl(media.externalHost, media.externalId);
      if (embedUrl) {
        this.#hideImageElement(imageEl);
        embedEl.hidden = false;

        const iframe = document.createElement('iframe');
        iframe.src = embedUrl;
        iframe.title = alt || '';
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        iframe.loading = 'lazy';
        embedEl.appendChild(iframe);
        return;
      }
    }

    if (imageEl && media.poster) {
      imageEl.hidden = false;
      this.#setResponsiveImage(imageEl, media.poster, alt);
      if (sizes) imageEl.sizes = sizes;
    }
  }

  #ensureDrawerVideoObserver() {
    if (this.#drawerVideoObserver) return;

    this.#drawerVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;

          if (entry.isIntersecting && !this.#prefersReducedMotion()) {
            video.play().catch(() => { });
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
  }

  #registerDrawerVideo(video) {
    this.#ensureDrawerVideoObserver();
    this.#drawerVideos.add(video);
    this.#drawerVideoObserver.observe(video);

    if (!this.#prefersReducedMotion()) {
      video.play().catch(() => { });
    }
  }

  #playVisibleDrawerVideos() {
    if (this.#prefersReducedMotion()) return;

    this.#drawerVideos.forEach((video) => {
      if (!video.hidden) video.play().catch(() => { });
    });
  }

  #clearDrawerVideos() {
    this.#drawerVideos.forEach((video) => {
      this.#drawerVideoObserver?.unobserve(video);
      video.pause();
      video.removeAttribute('src');
      video.load();
    });
    this.#drawerVideos.clear();
    this.#drawerVideoObserver?.disconnect();
    this.#drawerVideoObserver = null;
  }

  #setResponsiveImage(img, src, alt) {
    if (!img || !src) return;

    const base = src.split('?')[0];
    img.src = `${base}?width=${IMAGE_WIDTHS[0]}`;
    img.srcset = IMAGE_WIDTHS.map((width) => `${base}?width=${width} ${width}w`).join(', ');
    img.sizes = '8.75rem';
    img.alt = alt || '';
  }

  async #render() {
    const token = ++this.#renderToken;
    const signal = this.#renewFetchSignal();
    const listEl = this.querySelector('[data-favorites-list]');
    const emptyStateEl = this.querySelector('[data-favorites-empty-state]');
    const recentlyEl = this.querySelector('[data-favorites-recently-viewed]');
    const showRecentlyViewed = this.dataset.showRecentlyViewed === 'true';
    const loaderEl = this.querySelector('[data-favorites-loader]');

    if (!listEl) return;

    const favorites = getFavorites();

    if (favorites.length === 0) {
      this.#clearDrawerVideos();
      listEl.replaceChildren();
      listEl.hidden = true;
      if (emptyStateEl) emptyStateEl.hidden = false;
      loaderEl?.setAttribute('hidden', '');
      this.classList.add('favorites-drawer--empty');

      if (token !== this.#renderToken) return;

      if (showRecentlyViewed) await this.#renderRecentlyViewed(recentlyEl);

      if (token !== this.#renderToken) return;
      this.#renderedKey = this.#getFavoritesKey();
      this.refreshScrollableElement();
      this.#playVisibleDrawerVideos();
      return;
    }

    this.classList.remove('favorites-drawer--empty');
    if (emptyStateEl) emptyStateEl.hidden = true;
    this.#recentlyViewedAbortController?.abort();
    this.#hideRecentlyViewed(recentlyEl);

    loaderEl?.removeAttribute('hidden');
    this.#clearDrawerVideos();

    const rows = await Promise.all(
      favorites.map((item) => this.#buildItemRow(item, signal).catch(() => null)),
    );

    if (token !== this.#renderToken) return;

    const sortedRows = rows.filter(Boolean).sort((rowA, rowB) => {
      const aSoldOut = rowA.classList.contains('favorites-drawer__item--sold-out');
      const bSoldOut = rowB.classList.contains('favorites-drawer__item--sold-out');

      if (aSoldOut === bSoldOut) return 0;
      return aSoldOut ? 1 : -1;
    });

    const fragment = document.createDocumentFragment();
    sortedRows.forEach((row) => fragment.appendChild(row));

    listEl.replaceChildren(fragment);
    this.#playVisibleDrawerVideos();
    listEl.hidden = sortedRows.length === 0;
    if (listEl.hidden && emptyStateEl) {
      emptyStateEl.hidden = false;
      this.classList.add('favorites-drawer--empty');
    }

    loaderEl?.setAttribute('hidden', '');
    this.#renderedKey = this.#getFavoritesKey();
    this.refreshScrollableElement();
  }

  #buildStockFromVariant(variant) {
    if (!variant) return null;

    return {
      variantId: variant.id,
      available: variant.available,
      inventory_management: variant.inventory_management ?? null,
      inventory_policy: variant.inventory_policy ?? null,
      inventory_quantity: variant.inventory_quantity,
    };
  }

  #getVariantStock(variant) {
    const inlineStock = this.#buildStockFromVariant(variant);
    const hasInlineQuantity =
      inlineStock?.inventory_quantity != null &&
      Number.isFinite(Number(inlineStock.inventory_quantity));

    return hasInlineQuantity
      ? Promise.resolve(inlineStock)
      : this.#fetchVariantStock(variant.id);
  }

  #fetchVariantStock(variantId) {
    const cacheKey = String(variantId);
    const cached = this.#stockCache.get(cacheKey);

    if (cached?.expiresAt > Date.now()) {
      return Promise.resolve(cached.stock);
    }

    if (cached) {
      this.#stockCache.delete(cacheKey);
    }

    if (this.#stockRequests.has(cacheKey)) {
      return this.#stockRequests.get(cacheKey);
    }

    const localeRoot = window.routes.localeRootUrl || '';
    const params = new URLSearchParams({
      section_id: 'helper-favorites-stock',
    });

    const promise = fetch(
      `${localeRoot}/variants/${encodeURIComponent(cacheKey)}/?${params.toString()}`,
      { signal: this.#getStockFetchSignal() },
    )
      .then(async (response) => {
        if (!response.ok) throw new Error('Variant stock fetch failed');

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const jsonEl = doc.querySelector('[data-favorites-stock-json]');
        if (!jsonEl) throw new Error('Variant stock data missing');

        const stock = JSON.parse(jsonEl.textContent.trim());
        this.#stockCache.set(cacheKey, {
          stock,
          expiresAt: Date.now() + STOCK_CACHE_TTL_MS,
        });
        return stock;
      })
      .catch(() => null)
      .finally(() => {
        if (this.#stockRequests.get(cacheKey) === promise) {
          this.#stockRequests.delete(cacheKey);
        }
      });

    this.#stockRequests.set(cacheKey, promise);
    return promise;
  }

  // Deliberately NOT bound to the caller's AbortSignal. This promise is shared through
  // #productCache, so aborting one render used to reject the entry a LATER render was
  // already awaiting: it resolved to null, that render saw an empty product list and hid
  // the recently-viewed row (869ebqrax — the row vanished on the PDP, because opening a
  // product triggers a second render that inherited the first one's aborted promises).
  // Staleness is already handled by the callers' own token checks.
  #fetchProduct(handle) {
    if (this.#productCache.has(handle)) {
      return this.#productCache.get(handle);
    }

    const promise = fetch(
      `${window.routes.localeRootUrl || ''}/products/${encodeURIComponent(handle)}.js`,
    )
      .then((response) => {
        if (!response.ok) throw new Error('Product fetch failed');
        return response.json();
      })
      .catch((error) => {
        this.#productCache.delete(handle);
        throw error;
      });

    this.#productCache.set(handle, promise);
    return promise;
  }

  async #updateItemLowStockStatus(statusEl, variant) {
    const stock = await this.#getVariantStock(variant);
    if (!statusEl) return;

    if (isLowStock(stock)) {
      statusEl.textContent = this.#strings.low_stock || 'Low stock';
      statusEl.hidden = false;
      return;
    }

    statusEl.hidden = true;
  }

  async #buildItemRow(storedItem, signal) {
    const product = await this.#fetchProduct(storedItem.handle).catch(() => null);
    if (!product) return null;

    const storedVariant = product.variants.find(
      (entry) => String(entry.id) === String(storedItem.variantId)
    );

    if (!storedVariant) return null;

    const template = this.querySelector('#favorites-item-template');
    if (!template) return null;

    // A favorite saved without picking a size is product-level — its stored variant is
    // only the PDP fallback, so an in-stock sibling stands in before calling it sold out.
    const variant =
      hasSelectedVariant(storedItem) || storedVariant.available
        ? storedVariant
        : product.variants.find((entry) => entry.available) ?? storedVariant;

    const node = template.content.firstElementChild.cloneNode(true);
    const localeRoot = window.routes.localeRootUrl || '';
    const productUrl = `${localeRoot}/products/${product.handle}?variant=${variant.id}`;
    const isSoldOut = !variant.available;
    const media = await this.#getProductMedia(product, variant, signal);

    node.dataset.productId = String(product.id);
    node.dataset.productTitle = product.title || '';
    node.dataset.variantId = String(variant.id);
    node.dataset.price = String(Number(variant.price) / 100);
    node.dataset.productType = product.type || '';
    if (isSoldOut) node.classList.add('favorites-drawer__item--sold-out');

    const imageLink = node.querySelector('[data-favorites-image-link]');
    const image = node.querySelector('[data-favorites-image]');
    const video = node.querySelector('[data-favorites-video]');
    const embed = node.querySelector('[data-favorites-video-embed]');
    const titleLink = node.querySelector('[data-favorites-title-link]');
    const priceEl = node.querySelector('[data-favorites-price]');
    const comparePriceEl = node.querySelector('[data-favorites-compare-price]');
    const statusEl = node.querySelector('[data-favorites-status]');
    const optionsEl = node.querySelector('[data-favorites-options]');
    const removeBtn = node.querySelector('[data-action="remove-favorite"]');
    const secondaryBtn = node.querySelector('[data-favorites-secondary-action]');

    if (imageLink) imageLink.href = productUrl;
    this.#mountProductMedia({
      imageEl: image,
      videoEl: video,
      embedEl: embed,
      media,
      alt: product.title,
    });

    if (isSoldOut) video?.pause();

    if (titleLink) {
      titleLink.href = productUrl;
      titleLink.textContent = product.title;
    }
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }

    if (comparePriceEl) {
      const compareAt = Number(variant.compare_at_price);
      const price = Number(variant.price);

      if (Number.isFinite(compareAt) && compareAt > price) {
        comparePriceEl.textContent = formatMoney(compareAt);
        comparePriceEl.hidden = false;
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.hidden = true;
      }
    }

    if (statusEl) {
      if (isSoldOut) {
        statusEl.textContent = this.#strings.sold_out || 'Sold out';
        statusEl.hidden = false;
      } else {
        statusEl.hidden = true;
        void this.#updateItemLowStockStatus(statusEl, variant);
      }
    }

    if (optionsEl && storedItem.options) {
      optionsEl.replaceChildren();
      Object.entries(storedItem.options).forEach(([name, value]) => {
        if (!value) return;
        const li = document.createElement('li');
        li.className = 'favorites-drawer__option u-p4';
        li.textContent = `${name}: ${value}`;
        optionsEl.appendChild(li);
      });
    }

    if (removeBtn) {
      removeBtn.dataset.variantId = String(storedItem.variantId);
      removeBtn.setAttribute(
        'aria-label',
        `${this.#strings.remove || 'Remove'} — ${product.title}`,
      );
    }

    if (secondaryBtn) {
      secondaryBtn.dataset.variantId = String(storedItem.variantId);
      secondaryBtn.dataset.handle = product.handle || storedItem.handle || '';

      if (isSoldOut) {
        secondaryBtn.dataset.action = 'open-notify-me';
        secondaryBtn.dataset.productTitle = product.title || '';
        secondaryBtn.dataset.sizeLabel = this.#resolveFavoriteSizeLabel(storedItem.options);
        secondaryBtn.dataset.productData = JSON.stringify(product);
        secondaryBtn.removeAttribute('data-favorites-secondary-action');
        secondaryBtn.textContent = this.#strings.notify_me || 'Notify me';
        secondaryBtn.setAttribute(
          'aria-label',
          `${this.#strings.notify_me || 'Notify me'} — ${product.title}`,
        );
      } else {
        secondaryBtn.dataset.action = 'add-favorite-to-cart';
        secondaryBtn.setAttribute('data-favorites-secondary-action', '');
        secondaryBtn.removeAttribute('data-product-title');
        secondaryBtn.removeAttribute('data-size-label');
        secondaryBtn.removeAttribute('data-product-data');
        secondaryBtn.textContent = this.#strings.add_to_cart || 'Add to cart';
        secondaryBtn.setAttribute(
          'aria-label',
          `${this.#strings.add_to_cart || 'Add to cart'} — ${product.title}`,
        );
      }
    }

    return node;
  }

  #buildRecentSwiperSettings(count) {
    if (count <= 0) return null;

    return {
      slidesPerView: 2.476,
      freeMode: true,
      spaceBetween: 16,
      mousewheel: {
        forceToAxis: true,
      },
      centeredSlides: false,
      watchOverflow: true,
      breakpoints: {
        1025: {
          slidesPerView: 4,
          spaceBetween: 24,
          centeredSlides: false,
        },
      },
    };
  }

  async #fetchRecentSwiperSettings(count, signal) {
    const localeRoot = window.routes.localeRootUrl || '';
    const url = `${localeRoot}/?section_id=helper-favorites-recently-viewed&count=${count}`;

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Recent swiper settings fetch failed');

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const settingsEl = doc.querySelector('[data-favorites-recent-swiper-settings]');
      const settings = JSON.parse(settingsEl?.textContent.trim() || '{}');

      if (settings.slidesPerView != null) return settings;
    } catch (error) {
      if (this.#isAbortError(error)) return this.#buildRecentSwiperSettings(count);
      /* fall through */
    }

    return this.#buildRecentSwiperSettings(count);
  }

  async #buildRecentSlide(product, localeRoot, signal) {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';

    const link = document.createElement('a');
    link.className = 'favorites-drawer__recent-card';
    link.href = `${localeRoot}/products/${product.handle}`;

    const formattedPrice = formatMoney(product.price);
    const variant = product.variants?.find((entry) => entry.available) || product.variants?.[0];
    const media = await this.#getProductMedia(product, variant, signal);

    slide.setAttribute('data-list-item', '');
    slide.dataset.productId = String(product.id);
    slide.dataset.productTitle = product.title || '';
    slide.dataset.variantId = variant ? String(variant.id) : '';
    slide.dataset.price = String(Number(variant?.price ?? product.price) / 100);
    slide.dataset.productType = product.type || '';

    if (media) {
      const imageWrap = document.createElement('div');
      imageWrap.className = 'favorites-drawer__recent-image';

      const img = document.createElement('img');
      const video = document.createElement('video');
      video.className = 'favorites-drawer__video';
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.hidden = true;

      const embed = document.createElement('div');
      embed.className = 'favorites-drawer__video-embed';
      embed.hidden = true;

      this.#mountProductMedia({
        imageEl: img,
        videoEl: video,
        embedEl: embed,
        media,
        alt: product.title,
        sizes: '(max-width: 1024px) 50vw, 25vw',
      });

      imageWrap.append(img, video, embed);
      link.appendChild(imageWrap);
    }

    const defaultMeta = document.createElement('div');
    defaultMeta.className = 'favorites-drawer__recent-meta favorites-drawer__recent-meta--default';

    const defaultTitle = document.createElement('p');
    defaultTitle.className = 'favorites-drawer__recent-title u-p4';
    defaultTitle.textContent = product.title;
    defaultMeta.appendChild(defaultTitle);

    const defaultPrice = document.createElement('p');
    defaultPrice.className = 'favorites-drawer__recent-price u-p4';
    defaultPrice.textContent = formattedPrice;
    defaultMeta.appendChild(defaultPrice);

    link.dataset.recentTitle = product.title;
    link.dataset.recentPrice = formattedPrice;

    link.appendChild(defaultMeta);

    slide.appendChild(link);
    return slide;
  }

  #bindRecentHoverMeta(swiperWrap, hoverMeta, container) {
    const hoverLink = hoverMeta.querySelector('[data-favorites-recent-hover-link]');
    const hoverTitle = hoverMeta.querySelector('[data-favorites-recent-hover-title]');
    const hoverPrice = hoverMeta.querySelector('[data-favorites-recent-hover-price]');

    if (!hoverLink || !hoverTitle || !hoverPrice) return;

    const showHover = (card) => {
      const slide = card.closest('.swiper-slide') || card;

      hoverLink.href = card.href;
      hoverTitle.textContent = card.dataset.recentTitle || '';
      hoverPrice.textContent = card.dataset.recentPrice || '';
      PRODUCT_DATASET_KEYS.forEach((key) => {
        hoverLink.dataset[key] = slide.dataset[key] || '';
      });
      hoverLink.dataset.index = String(
        Array.prototype.indexOf.call(slide.parentElement?.children ?? [], slide)
      );
      container.classList.add('favorites-drawer__recently-viewed--has-hover');
    };

    const hideHover = () => {
      container.classList.remove('favorites-drawer__recently-viewed--has-hover');
    };

    swiperWrap.addEventListener('pointerover', (event) => {
      const card = event.target.closest('.favorites-drawer__recent-card');
      if (!card || !swiperWrap.contains(card)) return;
      showHover(card);
    });

    swiperWrap.addEventListener('pointerout', (event) => {
      const related = event.relatedTarget;
      if (!related || !swiperWrap.contains(related)) hideHover();
    });

    swiperWrap.addEventListener('focusin', (event) => {
      const card = event.target.closest('.favorites-drawer__recent-card');
      if (!card || !swiperWrap.contains(card)) return;
      showHover(card);
    });

    swiperWrap.addEventListener('focusout', (event) => {
      const related = event.relatedTarget;
      if (!related || !swiperWrap.contains(related)) hideHover();
    });
  }

  #createRecentHoverMeta() {
    const hoverMeta = document.createElement('div');
    hoverMeta.className = 'favorites-drawer__recent-hover-meta';
    hoverMeta.setAttribute('data-favorites-recent-hover-meta', '');

    const hoverLink = document.createElement('a');
    hoverLink.className = 'favorites-drawer__recent-hover-link';
    hoverLink.href = '#';
    hoverLink.setAttribute('data-favorites-recent-hover-link', '');
    hoverLink.setAttribute('data-list-item', '');

    const hoverTitle = document.createElement('span');
    hoverTitle.className = 'favorites-drawer__recent-title u-p4';
    hoverTitle.setAttribute('data-favorites-recent-hover-title', '');

    const hoverPrice = document.createElement('span');
    hoverPrice.className = 'favorites-drawer__recent-price u-p4';
    hoverPrice.setAttribute('data-favorites-recent-hover-price', '');

    hoverLink.append(hoverTitle, hoverPrice);
    hoverMeta.appendChild(hoverLink);

    return hoverMeta;
  }

  #showRecentlyViewedLoading(container) {
    const slideCount = 4;

    container.hidden = false;
    container.className = 'favorites-drawer__recently-viewed favorites-drawer__recently-viewed--loading';
    container.classList.remove('favorites-drawer__recently-viewed--visible');
    container.replaceChildren();

    const title = document.createElement('h3');
    title.className = 'favorites-drawer__recently-viewed-title u-sh4';
    title.textContent = this.#strings.recently_viewed || 'Recently viewed';

    const skeleton = document.createElement('div');
    skeleton.className = 'favorites-drawer__recent-skeleton';
    skeleton.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < slideCount; i += 1) {
      const slide = document.createElement('div');
      slide.className = 'favorites-drawer__recent-skeleton-slide';

      const image = document.createElement('div');
      image.className = 'favorites-drawer__recent-skeleton-image';
      slide.appendChild(image);

      skeleton.appendChild(slide);
    }

    container.append(title, skeleton);
    this.refreshScrollableElement();
  }

  #animateRecentlyViewedIn(container) {
    container.classList.remove('favorites-drawer__recently-viewed--loading');
    container.classList.remove('favorites-drawer__recently-viewed--visible');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.classList.add('favorites-drawer__recently-viewed--visible');
      });
    });
  }

  async #renderRecentlyViewed(container) {
    if (!container) return;

    const token = ++this.#recentlyViewedToken;
    const signal = this.#renewRecentlyViewedSignal();
    const handles = getRecentlyViewedHandles(FAVORITES_RECENTLY_VIEWED_LIMIT).filter(Boolean);

    if (handles.length === 0) {
      this.#hideRecentlyViewed(container);
      return;
    }

    this.#showRecentlyViewedLoading(container);
    if (token !== this.#recentlyViewedToken || signal.aborted) return;

    try {
      const localeRoot = window.routes.localeRootUrl || '';
      const products = (
        await Promise.all(
          handles.map((handle) => this.#fetchProduct(handle).catch(() => null)),
        )
      ).filter(Boolean);

      if (token !== this.#recentlyViewedToken || signal.aborted) return;

      if (products.length === 0) {
        this.#hideRecentlyViewed(container);
        return;
      }

      const count = Math.min(products.length, FAVORITES_RECENTLY_VIEWED_LIMIT);
      const swiperSettings = await this.#fetchRecentSwiperSettings(count, signal);
      if (token !== this.#recentlyViewedToken || signal.aborted) return;
      if (!swiperSettings) return;

      container.className = 'favorites-drawer__recently-viewed';
      container.classList.remove('favorites-drawer__recently-viewed--visible');
      this.#clearDrawerVideos();
      container.replaceChildren();

      const title = document.createElement('h3');
      title.className = 'favorites-drawer__recently-viewed-title u-sh4';
      title.textContent = this.#strings.recently_viewed || 'Recently viewed';

      const swiper = document.createElement('swiper-init');
      swiper.className = 'swiper favorites-drawer__recent-swiper';
      swiper.setAttribute('data-swiper-settings', JSON.stringify(swiperSettings));

      const wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';
      const slides = (
        await Promise.all(
          products
            .slice(0, FAVORITES_RECENTLY_VIEWED_LIMIT)
            .map((product) => this.#buildRecentSlide(product, localeRoot, signal).catch(() => null)),
        )
      ).filter(Boolean);

      if (token !== this.#recentlyViewedToken || signal.aborted) return;

      if (!slides.length) {
        this.#hideRecentlyViewed(container);
        return;
      }

      slides.forEach((slide) => wrapper.appendChild(slide));
      swiper.appendChild(wrapper);

      const swiperWrap = document.createElement('div');
      swiperWrap.className = 'favorites-drawer__recent-swiper-wrap';
      swiperWrap.appendChild(swiper);

      const hoverMeta = this.#createRecentHoverMeta();

      container.append(title, swiperWrap, hoverMeta);
      this.#bindRecentHoverMeta(swiperWrap, hoverMeta, container);

      this.#animateRecentlyViewedIn(container);
      this.refreshScrollableElement();
      this.#playVisibleDrawerVideos();
    } catch (error) {
      if (this.#isAbortError(error) || token !== this.#recentlyViewedToken || signal.aborted) {
        return;
      }

      this.#hideRecentlyViewed(container);
    }
  }

  #onRemove = (event, matchedButton) => {
    event.preventDefault();
    const button =
      matchedButton?.matches?.('[data-action="remove-favorite"]')
        ? matchedButton
        : event.target.closest('[data-action="remove-favorite"]');
    const variantId = button?.dataset.variantId;
    if (!variantId) return;
    removeFavorite(variantId);
  };

  #onSecondaryActionClick = (event, matchedButton) => {
    event.preventDefault();

    const button =
      matchedButton?.matches?.('[data-favorites-secondary-action]')
        ? matchedButton
        : event.target.closest('[data-favorites-secondary-action]');
    const action = button?.dataset.action;

    if (action === 'add-favorite-to-cart') {
      void this.#onAddToCart(event, button);
    }
  };

  #resolveFavoriteSizeLabel(options = {}) {
    if (!options || typeof options !== 'object') return '';

    const entries = Object.entries(options).filter(([, value]) => value);
    const isLengthOption = (name) =>
      /^(length|inseam|längd|lang)$/i.test(name) || /\blength\b/i.test(name);
    const isSizeOption = (name) =>
      /^size$/i.test(name) || (/\bsize\b/i.test(name) && !isLengthOption(name));

    for (const [name, value] of entries) {
      if (isSizeOption(name)) return String(value);
    }

    for (const [name, value] of entries) {
      if (!isLengthOption(name)) return String(value);
    }

    return entries[0]?.[1] ? String(entries[0][1]) : '';
  }

  #onAddToCart = async (event, matchedButton) => {
    event.preventDefault();

    const button =
      matchedButton?.matches?.('[data-action="add-favorite-to-cart"]')
        ? matchedButton
        : event.target.closest('[data-action="add-favorite-to-cart"]');
    const variantId = button?.dataset.variantId;

    if (!button || !variantId || button.disabled) return;

    const storedItem = getFavorites().find((item) => String(item.variantId) === String(variantId));
    const handle = button.dataset.handle || storedItem?.handle;

    if (!handle) return;

    button.disabled = true;

    try {
      const product = await this.#fetchProduct(handle);
      if (!product) return;

      // A favorite saved with its size picked already names one variant — send it straight
      // to the cart. Only the size-less ones still need the quick-add picker.
      if (hasSelectedVariant(storedItem) || !this.#shouldUseQuickAtc(product)) {
        await addVariantToCart(variantId, 1, { dispatchTarget: this });
        return;
      }

      this.#openQuickAtc(product, storedItem || { handle, variantId });
    } catch (error) {
      if (this.#isAbortError(error)) return;
      console.error('[favorites-drawer] add to cart failed', error);
    } finally {
      button.disabled = false;
    }
  };

  #shouldUseQuickAtc(product) {
    const variants = product?.variants || [];
    if (variants.length <= 1) return false;

    const optionNames = this.#getProductOptions(product).map((option) => option.name.toLowerCase());
    const hasDefaultTitleOnly = optionNames.length === 1 && optionNames[0] === 'title';

    return !hasDefaultTitleOnly;
  }

  #getProductOptions(product) {
    const raw = product?.options || [];

    return raw
      .map((entry, index) => {
        if (typeof entry === 'string') {
          return { name: entry, position: index + 1, values: null };
        }

        if (!entry || typeof entry !== 'object') return null;

        const name = entry.name || entry.title || '';
        const position = Number(entry.position) || index + 1;
        const values = Array.isArray(entry.values)
          ? entry.values
            .map((value) =>
              typeof value === 'string' ? value : value?.name || String(value ?? '')
            )
            .filter(Boolean)
          : null;

        if (!name) return null;

        return { name, position, values };
      })
      .filter(Boolean);
  }

  #onCloseQuickAtc = (event) => {
    event.preventDefault();
    this.#closeQuickAtc();
  };

  #onQuickAtcOptionChange = (event) => {
    if (
      event.target.dataset.optionPosition === this.#quickAtcDefaultConfig?.position
    ) {
      this.#quickAtcDefaultSelectionIsAutomatic = false;
    } else {
      this.#resolveQuickAtcDefaultSelection();
    }

    this.#updateQuickAtcOptionStates();
    this.#updateQuickAtcButton();
    void this.#updateQuickAtcStockStatus();
    this.#hideQuickAtcError();
  };

  #onQuickAtcSubmit = (event, matchedButton) => {
    event.preventDefault();

    const button = matchedButton?.matches?.('[data-favorites-quick-atc-submit]')
      ? matchedButton
      : event.target.closest('[data-favorites-quick-atc-submit]');

    if (!button) return;

    void this.#submitQuickAtc(button);
  };

  #getQuickAtcEl() {
    return this.querySelector('[data-favorites-quick-atc]');
  }

  #openQuickAtc(product, storedItem) {
    if (this.#quickAtcOpen) {
      this.#closeQuickAtc(true);
    }

    const root = this.#getQuickAtcEl();
    const titleEl = this.querySelector('[data-favorites-quick-atc-title]');
    const optionsEl = this.querySelector('[data-favorites-quick-atc-options]');
    if (!root || !titleEl || !optionsEl) return;

    this.#quickAtcProduct = product;
    this.#quickAtcStoredItem = storedItem;
    this.#quickAtcVariants = product.variants || [];
    this.#quickAtcSubmitting = false;
    this.#quickAtcDefaultSelectionIsAutomatic = false;

    titleEl.textContent = product.title || '';
    optionsEl.replaceChildren();
    this.#buildQuickAtcOptions(product, optionsEl);
    this.#hideQuickAtcError();
    this.#hideQuickAtcStockStatus();
    this.#updateQuickAtcButton();
    void this.#updateQuickAtcStockStatus();

    root.hidden = false;
    root.removeAttribute('inert');
    root.setAttribute('aria-hidden', 'false');

    // Flush styles so the closed state (panel translated down, backdrop transparent) is
    // the transition's start value. Adding --open in the same tick as unhiding leaves the
    // browser nothing to animate from, so the panel snapped open while the close animated
    // normally — the asymmetry in 869ebrx8f.
    void root.offsetHeight;
    root.classList.add('favorites-quick-atc--open');

    this.#quickAtcOpen = true;
  }

  #closeQuickAtc(immediate = false) {
    if (!this.#quickAtcOpen) return;

    const root = this.#getQuickAtcEl();
    const panel = root?.querySelector('.favorites-quick-atc__panel');
    if (!root) return;

    clearTimeout(this.#quickAtcCloseTimeout);
    this.#quickAtcCloseTimeout = null;
    this.#quickAtcStockRequestToken += 1;
    this.#hideQuickAtcStockStatus();

    const finalize = () => {
      root.classList.remove('favorites-quick-atc--open');
      root.hidden = true;
      root.setAttribute('inert', '');
      root.setAttribute('aria-hidden', 'true');
      this.querySelector('[data-favorites-quick-atc-options]')?.replaceChildren();
      this.#setQuickAtcSubmitLoading(false);
      this.#hideQuickAtcError();
      this.#hideQuickAtcStockStatus();
      this.#quickAtcProduct = null;
      this.#quickAtcStoredItem = null;
      this.#quickAtcVariants = [];
      this.#quickAtcSubmitting = false;
      this.#quickAtcDefaultSelectionIsAutomatic = false;
      this.#quickAtcOpen = false;
    };

    if (immediate) {
      finalize();
      return;
    }

    root.classList.remove('favorites-quick-atc--open');

    const onEnd = (event) => {
      if (event.target !== panel) return;
      panel.removeEventListener('transitionend', onEnd);
      clearTimeout(this.#quickAtcCloseTimeout);
      this.#quickAtcCloseTimeout = null;
      finalize();
    };

    panel?.addEventListener('transitionend', onEnd);
    this.#quickAtcCloseTimeout = setTimeout(() => {
      panel?.removeEventListener('transitionend', onEnd);
      finalize();
    }, 400);
  }

  #buildQuickAtcOptions(product, container) {
    this.#getProductOptions(product).forEach((option, index) => {
      const position = String(option.position || index + 1);
      const optionIndex = Number(position) - 1;
      const values = this.#getQuickAtcOptionValues(optionIndex, option);
      const fieldset = document.createElement('fieldset');

      fieldset.className = 'pdp-variant-option favorites-quick-atc__option';
      fieldset.dataset.optionPosition = position;
      fieldset.dataset.optionName = option.name;

      const legend = document.createElement('legend');
      legend.className = 'pdp-variant-option__title u-p5';
      const template = this.#strings.selectOption || 'Select {{ option }}:';
      legend.textContent = template.replace(/\{\{\s*option\s*\}\}/gi, option.name.toLowerCase());
      fieldset.appendChild(legend);

      const track = document.createElement('div');
      track.className = 'pdp-variant-option__track';
      const list = document.createElement('ul');
      list.className = 'pdp-variant-option__list';

      values.forEach((value, valueIndex) => {
        const li = document.createElement('li');
        li.className = 'pdp-variant-option__item';
        const inputId = `favorites-quick-atc-${product.id}-${position}-${valueIndex}`;
        const input = document.createElement('input');

        input.type = 'radio';
        input.className = 'pdp-variant-option__input VisuallyHidden';
        input.name = `favorites-quick-atc-${product.id}-${position}`;
        input.value = value;
        input.id = inputId;
        input.dataset.optionPosition = position;

        const label = document.createElement('label');
        label.htmlFor = inputId;
        label.className = 'pdp-variant-option__label u-p3 pdp-variant-option__label--default';
        label.textContent = value;

        li.append(input, label);
        list.appendChild(li);
      });

      track.appendChild(list);
      fieldset.appendChild(track);
      container.appendChild(fieldset);
    });

    container.querySelectorAll('.pdp-variant-option__track').forEach((track, index) => {
      const statusEl = document.createElement('p');
      statusEl.className = 'favorites-quick-atc__stock-status u-p5';
      statusEl.dataset.favoritesQuickAtcStockStatus = '';
      if (index === 0) {
        statusEl.setAttribute('role', 'status');
      } else {
        statusEl.setAttribute('aria-hidden', 'true');
      }
      statusEl.hidden = true;
      track.appendChild(statusEl);
    });

    this.#resolveQuickAtcDefaultSelection({ force: true });
    this.#updateQuickAtcOptionStates();
  }

  #resolveQuickAtcDefaultSelection({ force = false } = {}) {
    const config = this.#quickAtcDefaultConfig;
    if (!config) return;
    if (!force && !this.#quickAtcDefaultSelectionIsAutomatic) return;

    const fieldset = this.querySelector(
      `[data-favorites-quick-atc-options] .pdp-variant-option[data-option-position="${config.position}"]`,
    );
    if (!fieldset) return;

    this.#quickAtcDefaultSelectionIsAutomatic = true;

    const optionIndex = Number(config.position) - 1;
    const selected = this.#getQuickAtcSelectedOptions();
    const inputs = [...fieldset.querySelectorAll('.pdp-variant-option__input')];
    const nextInput = config.values
      .map((configuredValue) =>
        inputs.find(
          (input) => input.value.trim().toLowerCase() === configuredValue.toLowerCase(),
        ),
      )
      .find(
        (input) =>
          input && this.#isQuickAtcValueAvailable(optionIndex, input.value, selected),
      );

    inputs.forEach((input) => {
      input.checked = input === nextInput;
    });
  }

  #getQuickAtcOptionValues(optionIndex, option = null) {
    if (option?.values?.length) {
      return [...new Set(option.values)];
    }

    const values = [];
    const seen = new Set();

    this.#quickAtcVariants.forEach((variant) => {
      const value = variant.options?.[optionIndex];
      if (value && !seen.has(value)) {
        seen.add(value);
        values.push(value);
      }
    });

    return values;
  }

  #getQuickAtcSelectedOptions() {
    /** @type {Record<string, string | null>} */
    const selected = {};

    this.querySelectorAll('[data-favorites-quick-atc-options] .pdp-variant-option').forEach((fieldset) => {
      const position = fieldset.dataset.optionPosition;
      const checked = fieldset.querySelector('.pdp-variant-option__input:checked');
      selected[position] = checked?.value ?? null;
    });

    return selected;
  }

  #findQuickAtcVariant() {
    const selected = this.#getQuickAtcSelectedOptions();
    const fieldsets = this.querySelectorAll('[data-favorites-quick-atc-options] .pdp-variant-option');

    for (const fieldset of fieldsets) {
      const position = fieldset.dataset.optionPosition;
      if (!selected[position]) return null;
    }

    return (
      this.#quickAtcVariants.find((variant) =>
        variant.options.every((optionValue, index) => optionValue === selected[String(index + 1)])
      ) ?? null
    );
  }

  #isQuickAtcValueAvailable(optionIndex, value, selected) {
    return this.#quickAtcVariants.some((variant) => {
      if (!variant.available) return false;
      if (variant.options[optionIndex] !== value) return false;

      return variant.options.every((optionValue, index) => {
        if (index === optionIndex) return true;
        const position = String(index + 1);
        const selectedValue = selected[position];
        return !selectedValue || optionValue === selectedValue;
      });
    });
  }

  #updateQuickAtcOptionStates() {
    const selected = this.#getQuickAtcSelectedOptions();

    this.querySelectorAll('[data-favorites-quick-atc-options] .pdp-variant-option').forEach((fieldset) => {
      const position = Number(fieldset.dataset.optionPosition);
      const optionIndex = position - 1;

      fieldset.querySelectorAll('.pdp-variant-option__input').forEach((input) => {
        const label = fieldset.querySelector(`label[for="${input.id}"]`);
        if (!label) return;

        const isChecked = input.checked;
        const isAvailable = this.#isQuickAtcValueAvailable(optionIndex, input.value, selected);
        let state = 'default';

        if (!isAvailable) {
          state = 'sold-out';
        }

        if (isChecked) {
          state = !isAvailable ? 'sold-out-selected' : 'selected';
        }

        label.className = `pdp-variant-option__label u-p3 pdp-variant-option__label--${state}`;
      });
    });
  }

  #updateQuickAtcButton() {
    const button = this.querySelector('[data-favorites-quick-atc-submit]');
    if (!button || this.#quickAtcSubmitting) return;

    const variant = this.#findQuickAtcVariant();
    const hasOptions = this.querySelectorAll('[data-favorites-quick-atc-options] .pdp-variant-option').length > 0;

    if (!hasOptions) {
      this.#applyQuickAtcButtonState('ready');
      return;
    }

    if (!variant) {
      this.#applyQuickAtcButtonState('choose-size');
      return;
    }

    if (!variant.available) {
      this.#applyQuickAtcButtonState(this.#hasNotifyMe() ? 'notify-me' : 'sold-out');
      return;
    }

    this.#applyQuickAtcButtonState('ready');
  }

  async #updateQuickAtcStockStatus() {
    const requestToken = ++this.#quickAtcStockRequestToken;
    const variant = this.#findQuickAtcVariant();

    this.#hideQuickAtcStockStatus();
    if (!variant) return;

    if (!variant.available) {
      this.#showQuickAtcStockStatus(this.#strings.sold_out || 'Sold out');
      return;
    }

    const stock = await this.#getVariantStock(variant);

    if (requestToken !== this.#quickAtcStockRequestToken) return;
    if (String(this.#findQuickAtcVariant()?.id) !== String(variant.id)) return;

    if (isLowStock(stock)) {
      this.#showQuickAtcStockStatus(this.#strings.low_stock || 'Low stock');
    }
  }

  #showQuickAtcStockStatus(text) {
    this.querySelectorAll('[data-favorites-quick-atc-stock-status]').forEach((statusEl) => {
      statusEl.textContent = text;
      statusEl.hidden = false;
    });
  }

  #hideQuickAtcStockStatus() {
    this.querySelectorAll('[data-favorites-quick-atc-stock-status]').forEach((statusEl) => {
      statusEl.hidden = true;
      statusEl.textContent = '';
    });
  }

  #hasNotifyMe() {
    return Boolean(this.querySelector('pdp-notify-me'));
  }

  #getQuickAtcSelectedNamedOptions() {
    const selected = this.#getQuickAtcSelectedOptions();
    /** @type {Record<string, string>} */
    const options = {};

    this.querySelectorAll('[data-favorites-quick-atc-options] .pdp-variant-option').forEach((fieldset) => {
      const name = fieldset.dataset.optionName;
      const position = fieldset.dataset.optionPosition;
      const value = selected[position];
      if (name && value) options[name] = value;
    });

    return options;
  }

  #openQuickAtcNotifyMe(variant) {
    const notifyMe = this.querySelector('pdp-notify-me');
    if (!notifyMe?.open || !variant?.id) return;

    notifyMe.open({
      variantId: String(variant.id),
      productData: this.#quickAtcProduct,
      productTitle: this.#quickAtcProduct?.title || '',
      sizeLabel: this.#resolveFavoriteSizeLabel(this.#getQuickAtcSelectedNamedOptions()),
    });
  }

  #applyQuickAtcButtonState(state) {
    const button = this.querySelector('[data-favorites-quick-atc-submit]');
    if (!button) return;

    const textByState = {
      'choose-size': this.#strings.choose_size || 'Please, choose size',
      ready: this.#strings.add_to_cart || 'Add to cart',
      'notify-me': this.#strings.notify_me || 'Notify me',
      'sold-out': this.#strings.sold_out || 'Sold out',
      added: this.#strings.added || 'Added',
    };

    const text = textByState[state] ?? '';
    button.dataset.state = state;
    button.querySelectorAll('[data-favorites-quick-atc-submit-text], .button-txt').forEach((el) => {
      el.textContent = text;
    });
    button.setAttribute('aria-label', text);

    if (state === 'choose-size') {
      button.removeAttribute('disabled');
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    button.removeAttribute('aria-disabled');

    if (state === 'ready' || state === 'notify-me') {
      button.removeAttribute('disabled');
      return;
    }

    button.setAttribute('disabled', '');
  }

  #setQuickAtcSubmitLoading(visible) {
    const button = this.querySelector('[data-favorites-quick-atc-submit]');
    button?.classList.toggle('is-loading', visible);
  }

  #showQuickAtcError() {
    const errorEl = this.querySelector('[data-favorites-quick-atc-error]');
    if (!errorEl) return;

    errorEl.hidden = false;
    errorEl.classList.add('visible');
    clearTimeout(this.#quickAtcErrorTimeout);
    this.#quickAtcErrorTimeout = setTimeout(() => {
      this.#hideQuickAtcError();
    }, 2500);
  }

  #hideQuickAtcError() {
    const errorEl = this.querySelector('[data-favorites-quick-atc-error]');
    if (!errorEl) return;

    errorEl.hidden = true;
    errorEl.classList.remove('visible');
    clearTimeout(this.#quickAtcErrorTimeout);
    this.#quickAtcErrorTimeout = null;
  }

  async #submitQuickAtc(button = this.querySelector('[data-favorites-quick-atc-submit]')) {
    const state = button?.dataset.state;
    const variant = this.#findQuickAtcVariant();

    if (!button || state === 'choose-size' || this.#quickAtcSubmitting) return;

    if (state === 'notify-me') {
      this.#openQuickAtcNotifyMe(variant);
      return;
    }

    if (state === 'sold-out' || !variant?.available) return;

    this.#quickAtcSubmitting = true;
    this.#setQuickAtcSubmitLoading(true);
    button.setAttribute('disabled', '');
    this.#hideQuickAtcError();

    try {
      await addVariantToCart(String(variant.id), 1, { dispatchTarget: this });
      this.#applyQuickAtcButtonState('added');
      this.#setQuickAtcSubmitLoading(false);
      this.#closeQuickAtc();
    } catch (error) {
      if (this.#isAbortError(error)) return;
      this.#quickAtcSubmitting = false;
      this.#setQuickAtcSubmitLoading(false);
      button.removeAttribute('disabled');
      this.#updateQuickAtcButton();
      this.#showQuickAtcError();
    }
  }
}

if (!customElements.get('favorites-drawer')) {
  customElements.define('favorites-drawer', FavoritesDrawer);
}
